import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { format as formatDate, parseISO } from 'date-fns';
import { useToast } from '../hooks/useToast';
import { useMetricsLeads } from '../hooks/useMetricsLeads';
import {
  metricsLeadService,
  type MetricsLead,
  type MetricsLeadStatus,
} from '../services';
import authService from '../services/authService';

interface FiltersState {
  status?: MetricsLeadStatus;
  channel?: string;
  loyaltyCustomerId?: string;
  searchTerm?: string;
  createdFrom?: string;
  createdTo?: string;
  cnpj?: string;
}

interface UpdateDialogState {
  open: boolean;
  lead: MetricsLead | null;
  status: MetricsLeadStatus;
  notes: string;
  customerName: string;
  phone: string;
  email: string;
  loyaltyCustomerId: string;
}

const STATUS_OPTIONS: Array<{ value: MetricsLeadStatus; label: string; color: 'default' | 'success' | 'warning' | 'info' | 'error' }> = [
  { value: 'NEW', label: 'Novo', color: 'default' },
  { value: 'CONTACTED', label: 'Contactado', color: 'info' },
  { value: 'REACTIVATED', label: 'Reativado', color: 'success' },
  { value: 'DISQUALIFIED', label: 'Descartado', color: 'warning' },
];

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  try {
    const date = typeof value === 'string' ? parseISO(value) : new Date(value);
    return formatDate(date, 'dd/MM/yyyy HH:mm');
  } catch {
    return value ?? '-';
  }
};

const formatPhone = (value?: string) => {
  if (!value) return '-';
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) {
    return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  if (digits.length === 11) {
    return digits.replace(/^(\d{2})(\d)(\d{4})(\d{4})$/, '($1) $2$3-$4');
  }
  return value;
};

const MetricsLeadsPage: React.FC = () => {
  const { showError, showSuccess } = useToast();
  const user = authService.getUser();
  const [filters, setFilters] = useState<FiltersState>(() => ({
    status: 'NEW',
    cnpj: authService.getActiveCompanyCnpj() ?? user?.cnpj,
  }));
  const [updateDialog, setUpdateDialog] = useState<UpdateDialogState>({
    open: false,
    lead: null,
    status: 'NEW',
    notes: '',
    customerName: '',
    phone: '',
    email: '',
    loyaltyCustomerId: '',
  });
  const [isReactivatingId, setIsReactivatingId] = useState<string | null>(null);

  const queryParams = useMemo(
    () => ({
      status: filters.status,
      channel: filters.channel?.trim() || undefined,
      loyaltyCustomerId: filters.loyaltyCustomerId?.trim() || undefined,
      searchTerm: filters.searchTerm?.trim() || undefined,
      createdFrom: filters.createdFrom || undefined,
      createdTo: filters.createdTo || undefined,
      cnpj: filters.cnpj?.replace(/\D/g, '') || undefined,
      enabled: true,
    }),
    [filters],
  );

  const { data: leads, isLoading, isFetching, refetch, isError, error } = useMetricsLeads(queryParams);

  const handleFilterChange = <K extends keyof FiltersState>(key: K, value: FiltersState[K]) => {
    const normalizedValue = typeof value === 'string' ? (value.trim() === '' ? undefined : value) : value;
    setFilters((prev) => ({
      ...prev,
      [key]: (normalizedValue as FiltersState[K]) ?? undefined,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'NEW',
      cnpj: authService.getActiveCompanyCnpj() ?? user?.cnpj,
    });
  };

  const handleOpenUpdateDialog = (lead: MetricsLead) => {
    setUpdateDialog({
      open: true,
      lead,
      status: lead.status,
      notes: lead.notes ?? '',
      customerName: lead.customerName ?? '',
      phone: lead.phone ?? '',
      email: lead.email ?? '',
      loyaltyCustomerId: lead.loyaltyCustomerId ?? '',
    });
  };

  const handleCloseUpdateDialog = () => {
    setUpdateDialog((prev) => ({
      ...prev,
      open: false,
      lead: null,
    }));
  };

  const handleUpdateLead = async () => {
    if (!updateDialog.lead) {
      return;
    }

    try {
      const cnpj = filters.cnpj ?? authService.getActiveCompanyCnpj() ?? undefined;
      const updated = await metricsLeadService.updateLeadStatus(updateDialog.lead.id, {
        status: updateDialog.status,
        notes: updateDialog.notes.trim() || undefined,
        customerName: updateDialog.customerName.trim() || undefined,
        phone: updateDialog.phone.replace(/\D/g, '') || undefined,
        email: updateDialog.email.trim() || undefined,
        loyaltyCustomerId: updateDialog.loyaltyCustomerId.trim() || undefined,
        cnpj,
      });

      showSuccess('Lead atualizado com sucesso.');
      setUpdateDialog((prev) => ({ ...prev, open: false, lead: null }));
      void refetch();
      return updated;
    } catch (err: any) {
      const message = err?.message || 'Erro ao atualizar lead';
      showError(message);
    }
  };

  const handleReactivateLead = async (lead: MetricsLead) => {
    try {
      setIsReactivatingId(lead.id);
      const cnpj = filters.cnpj ?? authService.getActiveCompanyCnpj() ?? undefined;
      await metricsLeadService.reactivateLead(lead.id, {
        cnpj,
        referenceDate: new Date().toISOString(),
      });
      showSuccess('Lead reativado com sucesso.');
      void refetch();
    } catch (err: any) {
      const message = err?.message || 'Erro ao reativar lead';
      showError(message);
    } finally {
      setIsReactivatingId(null);
    }
  };

  const renderStatusChip = (status: MetricsLeadStatus) => {
    const option = STATUS_OPTIONS.find((opt) => opt.value === status);
    if (!option) {
      return <Chip label={status} variant="outlined" size="small" />;
    }
    return <Chip label={option.label} color={option.color} size="small" variant="outlined" />;
  };

  const renderLeadRow = (lead: MetricsLead) => {
    const handleWhatsappClick = () => {
      if (!lead.phone) {
        showError('Lead sem telefone cadastrado.');
        return;
      }
      const digits = lead.phone.replace(/\D/g, '');
      const url = `https://wa.me/55${digits}`;
      window.open(url, '_blank');
    };

    return (
      <TableRow key={lead.id} hover>
        <TableCell>
          <Typography variant="subtitle2">{lead.customerName || 'Sem nome'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {lead.email || 'Sem e-mail'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{formatPhone(lead.phone)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {lead.channel || '-'}
          </Typography>
        </TableCell>
        <TableCell>
          {lead.source && <Chip label={lead.source} size="small" sx={{ mr: 0.5, mb: 0.5 }} />}
          {lead.medium && <Chip label={lead.medium} size="small" sx={{ mr: 0.5, mb: 0.5 }} />}
          {lead.campaign && <Chip label={lead.campaign} size="small" sx={{ mr: 0.5, mb: 0.5 }} />}
        </TableCell>
        <TableCell>{renderStatusChip(lead.status)}</TableCell>
        <TableCell>
          <Typography variant="body2">Último contato: {formatDateTime(lead.lastInteractionAt)}</Typography>
          <Typography variant="caption" color="text.secondary">
            Criado em {formatDateTime(lead.createdAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Conversar no WhatsApp">
              <span>
                <IconButton size="small" color="success" onClick={handleWhatsappClick} disabled={!lead.phone}>
                  <WhatsAppIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Editar lead">
              <IconButton size="small" onClick={() => handleOpenUpdateDialog(lead)}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reativar campanha de fidelidade">
              <span>
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => handleReactivateLead(lead)}
                  disabled={isReactivatingId === lead.id}
                >
                  {isReactivatingId === lead.id ? (
                    <CircularProgress size={18} />
                  ) : (
                    <ReplayIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Leads e Micro CRM
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Acompanhe os leads capturados pelos canais digitais, atualize o status de contato e reative clientes na
        fidelidade.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(4, minmax(0, 1fr))',
            },
          }}
        >
          <Box>
            <TextField
              select
              label="Status"
              value={filters.status ?? ''}
              onChange={(event) => handleFilterChange('status', event.target.value as MetricsLeadStatus)}
              fullWidth
            >
              <MenuItem value="">Todos</MenuItem>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box>
            <TextField
              label="Canal"
              value={filters.channel ?? ''}
              onChange={(event) => handleFilterChange('channel', event.target.value)}
              fullWidth
              placeholder="Ex: landing, instagram"
            />
          </Box>
          <Box>
            <TextField
              label="Cliente fidelidade"
              value={filters.loyaltyCustomerId ?? ''}
              onChange={(event) => handleFilterChange('loyaltyCustomerId', event.target.value)}
              fullWidth
              placeholder="ID do cliente"
            />
          </Box>
          <Box>
            <TextField
              label="Busca geral"
              value={filters.searchTerm ?? ''}
              onChange={(event) => handleFilterChange('searchTerm', event.target.value)}
              fullWidth
              placeholder="Nome, telefone ou e-mail"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box>
            <TextField
              label="CNPJ"
              value={filters.cnpj ?? ''}
              onChange={(event) => handleFilterChange('cnpj', event.target.value)}
              fullWidth
              placeholder="Somente números"
              inputProps={{ maxLength: 18 }}
            />
          </Box>
          <Box>
            <TextField
              label="Criado a partir de"
              type="date"
              value={filters.createdFrom ?? ''}
              onChange={(event) => handleFilterChange('createdFrom', event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarTodayIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box>
            <TextField
              label="Criado até"
              type="date"
              value={filters.createdTo ?? ''}
              onChange={(event) => handleFilterChange('createdTo', event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarTodayIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button variant="outlined" color="inherit" onClick={handleClearFilters} startIcon={<CloseIcon />}>
              Limpar
            </Button>
            <Button variant="contained" onClick={() => refetch()} startIcon={<RefreshIcon />}>
              Atualizar
            </Button>
          </Box>
        </Box>
      </Paper>

      {isError && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <Paper>
        <Box display="flex" justifyContent="space-between" alignItems="center" px={3} py={2}>
          <Typography variant="h6">Leads ({leads?.length ?? 0})</Typography>
          {isFetching && <CircularProgress size={24} />}
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
             <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Datas</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : leads && leads.length > 0 ? (
                leads.map((lead) => renderLeadRow(lead))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box py={4}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum lead encontrado com os filtros selecionados.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={updateDialog.open} onClose={handleCloseUpdateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Atualizar lead</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label="Status"
              value={updateDialog.status}
              onChange={(event) =>
                setUpdateDialog((prev) => ({
                  ...prev,
                  status: event.target.value as MetricsLeadStatus,
                }))
              }
              fullWidth
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Nome"
              value={updateDialog.customerName}
              onChange={(event) =>
                setUpdateDialog((prev) => ({
                  ...prev,
                  customerName: event.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Telefone"
              value={updateDialog.phone}
              onChange={(event) =>
                setUpdateDialog((prev) => ({
                  ...prev,
                  phone: event.target.value,
                }))
              }
              fullWidth
              placeholder="Somente números"
            />
            <TextField
              label="E-mail"
              type="email"
              value={updateDialog.email}
              onChange={(event) =>
                setUpdateDialog((prev) => ({
                  ...prev,
                  email: event.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Cliente fidelidade"
              value={updateDialog.loyaltyCustomerId}
              onChange={(event) =>
                setUpdateDialog((prev) => ({
                  ...prev,
                  loyaltyCustomerId: event.target.value,
                }))
              }
              fullWidth
              placeholder="ID do cliente na fidelidade"
            />
            <TextField
              label="Notas"
              value={updateDialog.notes}
              onChange={(event) =>
                setUpdateDialog((prev) => ({
                  ...prev,
                  notes: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleUpdateLead} variant="contained">
            Salvar alterações
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MetricsLeadsPage;
